// Socket / multiplayer message wiring for the game board, extracted
// from GameBoard.jsx. setupSocketListeners registers every inbound
// handler (state sync, presence, card moves, piles, combat relay,
// chat, harbinger, masks) against the live MatchConnection; handlers
// mutate board state through the same host primitives the local
// player's actions use, so both clients run identical recipes.
//
// Host contract: the full GameBoard surface. This module is a
// registry of closures over `host`, not a rules engine. Game-rule
// logic stays in engine/; anything new here should only translate a
// wire message into existing host primitive calls.

import { UI, playUI } from '../../../utils/arena/uiSounds';
import { addTween, animateCardToPile } from '../../../utils/game/animations';
import { createPileMesh } from '../../../utils/game/cardMesh';
import { applyBoardLegalityFromMirror } from './boardLegality';
import { revertPendingCastNow, revertPendingMoveNow } from './pendingMoves';
import { PHASE, mergeUnitHandViews, resolveAuraAnchor, tagCastLock } from '../../../utils/game/engine/core';
import { computeGridPositions } from '../../../utils/game/grid';
import { isAuthMatchEnabled } from '../../../utils/game/net/authMatchFlag';
import { applyRemotePoseAndRest, removeCardBody, syncMeshFromBody } from '../../../utils/game/physicsWorld';
import { disconnectSocket, onMatchCombat, onMatchDiff, onMatchFull, onMatchIntentRejected, onOpponentDropped, onPlayerJoined, onPlayerLeft, onServerReconnect, requestMatchFull } from '../../../utils/game/socketClient';
import { ClientMirror } from '../../../utils/game/net/clientMirror';
import { applyTurnPhaseFromMirror, rollStateFromMirror } from './authTurnPhase';
import { applyResourcesFromMirror } from './authResources';
import { applyEffectLedgerFromMirror } from './authLedger';
import { assertReconcilerParity } from './authParity';
import { scheduleRenderParityCheck } from './renderParity';
import { applyRevealsFromMirror } from './authReveals';
import { applyCardStateFromMirror } from './authCardState';
import { applyPendingChoiceFromMirror, applyIgniteWindowFromMirror } from './authChoice';
import { turnBanner } from './logbookChat';
import { hydrateUnitHandSpells, applyZonesFromMirror, applyRevealedPilesFromMirror, applyHandFromMirror, applyHandAdditionsFromMirror, applyHandLeavesFromMirror, applyOwnPilesFromMirror, syncStorylineFromMirror, applyPositionsFromMirror, applyLivePositionsFromMirror, rebuildBoardFromMirror, scrubUnknownBoardMeshesFromMirror, applyBoardExitsFromMirror, applyCarriedExitsFromMirror, reconcilePileRenderToMirror, adoptOwnPileOrderFromMirror, scheduleReleaseReconcile } from './authZones';
import { applyMulliganDoneFromMirror } from './mulliganController';
import { applyBoardFxFromMirror } from './boardFx';
import { applyDiceFromMirror } from './authDice';
import { announceDicePalette } from './dicePaletteSync';
import { applyCombatFromMirror, applyGameOverFromMirror, onAuthCombatEvents, onReplayCombatEnvelope } from './authCombat';
import { isDevBuild } from '../../../utils/devMode';
import { devLog, setDevLogMatch, installDevErrorHook } from '../../../utils/game/net/devLog';
import { playSound } from '../../../utils/game/sounds';
import { getGameGrid } from '../../../utils/game/spawnConfig';
import { perf } from '../../../utils/perfMonitor';
import { toast } from 'sonner';
import { k, t } from '../../../localization/t';
import { msg } from '../../../utils/game/logMessage';

// Turn boundary log lines ("Ended turn" + the turn banner), derived from the
// turn-flip diff by EVERY client (qa#77 retest): the outgoing client used to
// author + broadcast them before PASS_TURN was even sent, which placed the
// banner ABOVE the server's end-of-turn automation lines, so those effects
// read as if they resolved on the incoming player's turn. Derived from the
// fold they land after the automation log frames of the same commit. Uses
// addLogEntry (not logAction/logSystem) so nothing re-broadcasts: each client
// derives its own copy from the same diff. The caller establishes that a turn
// boundary actually happened (globalTurn moved: currentTurn alone misses a
// skipped turn, where the pointer double-flips back to the same seat).
export function logTurnBoundaryFromFold(host, prevTurn, newTurn, mirrorState) {
  const outgoingIsLocal = !host.props.isSpectating
    && (host.props.isCasualSandbox || prevTurn === host._localSeat());
  host.addLogEntry(msg('Ended turn'), outgoingIsLocal ? 'you' : 'opponent', 'action');
  const nextName = host._displayNameForPlayer(newTurn);
  // Mirror only: host.state.turnNumbers is guaranteed STALE here (the fold's
  // setState has not flushed), so a fallback to it would print the pre-flip
  // count and hide a missing mirror field instead of surfacing it.
  const turnNo = mirrorState?.phase?.turnNumbers?.[newTurn];
  host.addLogEntry(turnBanner(nextName, turnNo), 'system', 'system');
}

// The state->scene fold funnel: folds an inbound { full | diff } wire message
// into the ClientMirror, then maps the folded state onto the board through
// every auth* reconciler. Shared verbatim by the live socket (onMatchDiff /
// onMatchFull) and replay playback (a ReplayDriver feeding the same message
// shapes). `live: false` suppresses the two steps that ACT on the session
// rather than render it: arming the first-player dice modal and running the
// incoming turn's START phase.
export function createFoldAndApply(host, mirror, { live = true } = {}) {
  // Isolate each fold step: one thrown step must never abort the REST of the
  // diff fold (a partial fold reads as a desync. E.g. the recalc crash that
  // bounced freshly played cards back to hand). The mirror itself already
  // folded the patches, so surviving steps keep working off correct state;
  // the failure is logged with its step name for the dev logs.
  const safeStep = (label, fn) => {
    try { fn(); } catch (err) { console.error(`[fold:${label}]`, err); }
  };
  const foldAndApply = (msg) => {
    if (host._unmounted) return;
    const prevTurn = host.state.currentTurn;
    const prevRollState = host.state.firstPlayerRollState;
    // Pre-fold mirror phase: the authoritative "before" for the turn
    // boundary log (host.state is a render copy that may not have flushed,
    // so two diffs folded in one batch would both read the stale turn).
    const prevPhase = mirror.getState()?.phase || null;
    mirror.applyInbound(msg);
    const m = mirror.getState();
    safeStep('turnPhase', () => applyTurnPhaseFromMirror(host, m));
    // Mulligan completion folds FROM the server confirm (never optimistic):
    // when the mirror marks one of our seats mulliganDone, flip the local
    // banner to 'done' and run the view hooks. A lost mulligan intent just
    // leaves the banner up for a retry. The gate can never wedge.
    safeStep('mulliganDone', () => applyMulliganDoneFromMirror(host, m));
    // Resources domain R1: fold authoritative life/mana/thresholds/DD onto
    // gameState.trackers. Idempotent. The actor's own seat already matches
    // (set before the emit), the opponent's seat updates from the diff.
    safeStep('resources', () => applyResourcesFromMirror(host, m));
    // EffectLedger (Phase A, A2): fold the authoritative cost-modifier / threshold /
    // mana ledger so the cost glow, threshold pool, and buff panel render from
    // server truth. Runs before the threshold-dependent folds; idempotent per diff.
    safeStep('effectLedger', () => applyEffectLedgerFromMirror(host, m));
    // Zones domain R2c: fold the authoritative OPPONENT pile/hand COUNTS onto
    // the opponent's placeholder stacks + hand fan (the server withholds the
    // identities). Idempotent + skips the single-writer local seat; a draw on
    // the actor's screen shows here only as the opponent's deck/hand count.
    safeStep('zones', () => applyZonesFromMirror(host, m));
    // Reveals domain R2d: the opponent's shared (eye-toggled) Collection is
    // now in our projection. Build/refresh/remove its synthetic pile so the
    // collection tab renders it. Rebuilt on the reconnect Full → survives.
    safeStep('revealedPiles', () => applyRevealedPilesFromMirror(host, m));
    // RC2: on a reconnect/resume Full, rebuild OUR own hand from the
    // authoritative mirror (the legacy snapshot can restore it stripped/
    // art-less). A no-op in live play (the diff path keeps the hand intact).
    if (msg?.kind === 'full') {
      // The authoritative Full landed: stop the connection's pull-retry
      // loop and release the first-player-dice gate (the Full carries the
      // real matchPhase/rollOff, so the arm check below reads truth, never
      // a cold-mount default).
      host.conn?.noteAuthoritativeStateArrived?.();
      host._authSnapshotPending = false;
      clearTimeout(host._authSnapshotGateTimer);
      // Not a fold: the match is attached, so this seat's dice palette goes
      // out to the server now (skipped when the mirror already carries it).
      if (live) safeStep('dicePalette', () => announceDicePalette(host, m));
      // Defer the own-hand + own-pile rebuild to AFTER React flushes so
      // any in-flight local setState lands first and the mirror. The
      // authoritative source. Stays the final word.
      host.setState({}, () => {
        safeStep('handFull', () => applyHandFromMirror(host, m));
        safeStep('ownPilesFull', () => applyOwnPilesFromMirror(host, m));
        // Recreate in-play card meshes the legacy snapshot didn't restore
        // (cards PLAYED during the match) from the authoritative mirror, THEN
        // snap every in-play mesh to its server coords.
        safeStep('boardRebuild', () => rebuildBoardFromMirror(host, m));
        // Solo resume: the fresh mount spawned the re-minted room's decks
        // (new ids) and this Full carries the durable board (original ids)
        // Drop the phantom just-spawned board meshes the mirror doesn't
        // know (the stacked duplicate-avatar-after-resume bug).
        if (host.props?.isCasualSandbox) {
          safeStep('boardScrubFull', () => scrubUnknownBoardMeshesFromMirror(host, m));
        }
        safeStep('positionsFull', () => applyPositionsFromMirror(host, m));
        // Re-fold the durable pendingChoice AFTER the rebuild: a pile prompt
        // over board/collection cards (Flanking's subset, Erik's Curiosa)
        // needs the just-rebuilt instances to open its picker. The earlier
        // synchronous fold ran before they existed.
        safeStep('pendingChoiceFull', () => applyPendingChoiceFromMirror(host, m));
        // Avatar of Fire: re-derive the local ignite UX flag from the
        // server's _fireballIgnite window (the flag is client-local; the
        // window is authoritative and survives the reload).
        safeStep('igniteWindow', () => applyIgniteWindowFromMirror(host, m));
      });
    }
    // The SERVER drives the whole start choreography (preGame auto-advance,
    // opening deal). The client's only start jobs on a fold: close a stale
    // dice modal once the roll is decided, and open the mulligan prompt when
    // the dealt hand has landed.
    safeStep('staleDice', () => host._maybeCloseStaleDiceModal?.());
    // Never in replay: the earliest frames sit in openingDraw, and folding
    // them would open the mulligan keep/redraw prompt for the viewed seat.
    if (live) safeStep('openingDraw', () => host._maybeRunOpeningDraw?.());
    // Reveals domain R2d: fold authoritative reveals (face-down flip, scry,
    // tutor/search) onto the OPPONENT's card meshes. The server un-strips an
    // identity for the entitled seat only, so a card the local mesh shows
    // blank but the mirror now names is re-hydrated + repainted here.
    // Idempotent + skips the single-writer local seat.
    safeStep('reveals', () => applyRevealsFromMirror(host, m));
    // Card-state domain D4: fold authoritative tap / control-flip onto the
    // rendered meshes. Idempotent over all cards. The actor's own already
    // match (optimistic local apply), the other client flips here.
    safeStep('cardState', () => applyCardStateFromMirror(host, m));
    // W4: server-derived placement/detach legality (banned site cells /
    // spawn extensions / locked equipment). Stored verbatim; refreshes the
    // drag halos when the bag changes mid-drag.
    safeStep('boardLegality', () => applyBoardLegalityFromMirror(host, m));
    // Automation player-choice (P6 cutover): fold the authoritative pendingChoice
    // into the chooser's local picker (highlights candidates; the next click
    // sends chooseIntent). Cleared when the server resolves/clears the prompt.
    safeStep('pendingChoice', () => applyPendingChoiceFromMirror(host, m));
    // Owner-locked unit hands (special spellcasters): the server stores the
    // full map and projects it per-seat (own + seen spells full, others backs).
    // Merge like the legacy relay so a just-stolen hand doesn't blank, then
    // re-derive controllers from card control. Gated so a normal match (no unit
    // hands) never churns setState.
    if (m?.unitHands && (Object.keys(m.unitHands).length || Object.keys(host.state.gameState.unitHands || {}).length)) {
      // Owner-locked draw choreography: on a LIVE diff (never a reconnect
      // Full), fly + sound each newly-added unit-hand spell onto its badge.
      // MUST run before the merge so it can diff against the pre-merge state.
      if (msg?.kind === 'diff') safeStep('unitHandDraws', () => host._animateUnitHandAdditions?.(m.unitHands));
      host.setState(
        (state) => ({
          gameState: {
            ...state.gameState,
            // Hydrate AFTER the merge: the mirror's spell objects are
            // frozen by Immer and carry only { id, cardId, locks }. Used
            // verbatim, the drag path threw on `spell.x =` (object not
            // extensible) and every render outside the badge showed a
            // card back (no name/imageUrl). Each entitled spell becomes a
            // fresh mutable instance enriched from the catalog.
            unitHands: hydrateUnitHandSpells(
              host,
              mergeUnitHandViews(m.unitHands, Object.fromEntries(Object.entries(state.gameState.unitHands || {}).filter(([id]) => m.unitHands[id]))),
            ),
          },
        }),
        () => host._reconcileUnitHandControllers?.(),
      );
    }
    // Card POSITIONS: the live drag + smooth landing ride the cosmetic
    // server-relayed presence channel (claim/stream/release); the SET_POSITION
    // command is the authoritative RECORD, reconciled from the mirror on a
    // reconnect Full (applyPositionsFromMirror in the Full branch above).
    // Server-driven in-play moves (Wildfire spread, Thunderstorm step,
    // Bladedancer step, persecutor) fold LIVE here. For cards not under an
    // active drag/ownership claim only, so the presence relay still owns
    // player drags and their settled landing.
    safeStep('livePositions', () => applyLivePositionsFromMirror(host, m));
    // S4. A pending move (drop → intent + ghost) is CONFIRMED once the mirror
    // shows the card on its requested cell; the livePositions fold above has
    // already relocated the mesh, so this just clears the transient ghost.
    safeStep('pendingMoves', () => host._resolvePendingMovesFromMirror?.(m));
    // S5. A pending cast (fresh play → cast intent + optimistic mesh) is
    // CONFIRMED once the mirror shows the card in zone 'play'; drop its marker
    // so the reject sweep never fires for it.
    safeStep('pendingCasts', () => host._resolvePendingCastsFromMirror?.(m));
    // Server-driven board exits: a unit the server moved play→cemetery/banished
    // (combat death, destroy) flies to its pile here. Runs after the card-state
    // fold so a card that only changed flags isn't mistaken for an exit.
    safeStep('boardExits', () => applyBoardExitsFromMirror(host, m));
    // A card that became carried (now inside a bearer's equippedCards) stays
    // zone 'play', so the exits fold above never drops its loose mesh. Do it
    // here, BEFORE the entries fold, so a mesh the relay already removed
    // can't be respawned on its old cell and a replay (no relay) never shows
    // the artifact on the site and the bearer at once.
    safeStep('carriedExits', () => applyCarriedExitsFromMirror(host, m));
    // Server-driven board ENTRIES (pure-view binding): any zone-play card the
    // diff carries with no local mesh spawns here LIVE. Server-minted tokens
    // (rubble / summon / conjure) AND cast cards, so the opponent's board is a
    // function of the authoritative diff, never of the legacy card:place relay
    // racing it. Idempotent both ways against the relay (mesh-exists skips).
    safeStep('boardEntries', () => rebuildBoardFromMirror(host, m));
    // Server-orchestrated board animations: play any new boardFx manifest
    // (both clients render the identical record). A Full marks records seen
    // without replaying (the restored zones already show the outcome).
    safeStep('boardFx', () => applyBoardFxFromMirror(host, m, { isFull: msg?.kind === 'full' }));
    // Table dice are MatchState: spawn, remove, glide and tumble them from the
    // mirror. After boardEntries so a die lands on the card it rests on.
    safeStep('dice', () => applyDiceFromMirror(host, m, { isFull: msg?.kind === 'full' }));
    // Render reconciler (pure-view binding): drop any card the mirror has moved
    // OUT of a pile it is still rendered in (e.g. the opponent pulled a card
    // from THEIR cemetery → our cemetery render must drop it this diff, not at
    // the next reload). Runs every diff for both seats' visible piles, so a
    // missing incremental pile fold self-heals. Board→pile entries are handled
    // by applyBoardExitsFromMirror above; this is the complementary leave path.
    safeStep('pileReconcile', () => reconcilePileRenderToMirror(host, m));
    // ...and then adopt the server's ORDER for our own draw piles (#53). The
    // reconciler above is membership-only, so a server-committed shuffle (P7
    // substitutes its own permutation for the client's) never reached the
    // render until a reload. Runs after it, once membership agrees: the
    // adopter is a no-op whenever the id sets still differ.
    safeStep('pileOrderAdopt', () => adoptOwnPileOrderFromMirror(host, m));
    // LIVE own-hand additions: a server-autonomous draw/deal (delegated draw
    // op, Howl keepers) lands in OUR hand from the diff. The local seat's
    // hand was previously Full-only. Additions-only, so it never fights an
    // optimistic local removal. MUST run AFTER reconcilePileRenderToMirror:
    // a server-drawn card still rendered in the local deck pile is skipped
    // by the addition's rendered-in-a-pile guard, and the reconciler is what
    // drops that pile render. Folding in the old order left the card
    // rendered NOWHERE until an unrelated later diff (the draw-vanishes
    // presentation bug the render-parity checker caught).
    safeStep('handAdditions', () => applyHandAdditionsFromMirror(host, m));
    // LIVE own-hand leaves: a card the server moved OUT of our hand (a
    // server-paid discard cost (Sisters of Avalon, Wizard's Den) landing
    // in the cemetery) is dropped from the hand fan. MUST run AFTER
    // reconcilePileRenderToMirror: the leave fold only drops a hand card
    // the mirror places in a zone that is ALREADY rendered elsewhere, and
    // the reconciler is what places the cemetery copy (qa#81, the
    // discard-ghost-in-hand bug).
    safeStep('handLeaves', () => applyHandLeavesFromMirror(host, m));
    // Focused-cell fan: re-derive the open fan from the folded state. A
    // member that left play flies out, an arrival fans in, an emptied cell
    // collapses, and a folded server prompt collapses it so every
    // candidate is clickable. Runs after every board fold above so it
    // always sees the settled post-diff board.
    safeStep('cellFocus', () => host._refreshCellFocus?.());
    // Combat (auth path): render the prompt from the authoritative
    // state.combat record. Runs AFTER the card-state fold so taps/deaths are
    // already on the board when the prompt opens. Idempotent (sig-guarded).
    safeStep('combat', () => applyCombatFromMirror(host, m));
    // Authoritative game-over: a combat resolution that drops a player to 0
    // life stamps `state.over`; fold it into the result flow so a
    // no-interaction lethal (Jinn site/avatar hit, double-KO) reaches game-over
    // instead of freezing both clients post-combat. Idempotent (re-broadcast +
    // reconnect Full both no-op via the _gameOverWinner guard).
    safeStep('gameOver', () => applyGameOverFromMirror(host, m));
    // Public storyline: render the opponent's dwelling cast + ack from the
    // mirror (live), and re-derive our own pending prompt on a Full (reload).
    safeStep('storyline', () => syncStorylineFromMirror(host, m, { isFull: msg?.kind === 'full' }));
    // Roll-off choreography: while the authoritative match is still in
    // rollOff, push the server's freshly-arrived rolls + first-player
    // choice through the modal's existing roll/choice subscribers. The
    // mapper has already converted seats to 1|2 and written the new
    // firstPlayerRollState onto host.state, so we replay against that.
    // replayInitiativeFields only fires for fields that newly became
    // known (diffed against prevRollState), so the modal animates the
    // server values and resolves the winner via its own checkResolution.
    if (m?.phase?.matchPhase === 'rollOff') {
      // The mirror says the roll-off is live. Make sure the dice modal is
      // up (cold mount arms HERE, once server truth has landed; the 1s mount
      // timer defers to this under auth so a mid-match reload can never
      // arm the dice off a default-null client rollState). Never in replay:
      // a seek through the prelude frames must not pop a dice modal over a
      // roll that finished long ago.
      if (live) safeStep('armDice', () => host._maybeArmFirstPlayerDice?.());
      const newRollState = rollStateFromMirror(m);
      if (newRollState) host._replayInitiativeFields(newRollState, prevRollState);
    }
    // Live turn-pass choreography: a DIFF (not a resume Full) that flips
    // currentTurn to US means a turn just passed and WE are the incoming
    // player. Run our START phase. The authoritative PASS_TURN replaces the
    // legacy turn:pass that used to carry this trigger (mirrors the START run
    // in the turn:pass handler below). A Full is resume/convergence, not a
    // live pass, so it never re-runs START.
    // Gated on matchPhase 'playing': during game-start the SET_FIRST_PLAYER
    // diff also flips currentTurn to the going-first seat, but that seat's
    // first START is owned by afterMulliganComplete (post opening-draw), not
    // this trigger. Without the guard a guest-goes-first roll fired START
    // mid-rollOff (premature draw). In 'playing' the turn-pass trigger fires
    // START on the incoming seat as before.
    if (msg?.kind === 'diff' && m?.phase?.matchPhase === 'playing') {
      const newTurn = m?.phase?.currentTurn;
      // Solo: the pilot IS the incoming player on every flip. The fold's
      // applyTurnPhaseFromMirror just swapped activePlayerNum to the
      // incoming seat, and the START phase runs for it here.
      const localSeat = host.state.isHost ? 'p1' : 'p2';
      const incomingIsLocal = host.props.isCasualSandbox || newTurn === localSeat;
      // A turn boundary is a globalTurn move on the PRE-fold mirror (a
      // skipped turn double-flips the pointer back to the same seat, so
      // currentTurn alone misses it; host.state is a stale render copy).
      // No boundary lines when the same diff ended the match: "GAME OVER"
      // followed by a banner for a turn nobody will play reads wrong.
      const boundaryCrossed = prevPhase
        && (prevPhase.globalTurn != null && m?.phase?.globalTurn != null
          ? m.phase.globalTurn !== prevPhase.globalTurn
          : (newTurn && newTurn !== prevPhase.currentTurn));
      if (boundaryCrossed && !m?.over) {
        safeStep('turnBoundaryLog', () => logTurnBoundaryFromFold(host, prevPhase.currentTurn, newTurn, m));
      }
      if (newTurn && newTurn !== prevTurn && incomingIsLocal && live) {
        // Defer START until applyTurnPhaseFromMirror's setState (currentTurn /
        // turnNumbers / currentPhase) has FLUSHED. Running it synchronously
        // here reads the STALE host.state.turnNumbers (Preact batches setState),
        // so the draw step saw the incoming player's turn count one behind.
        // their 2nd turn looked like their 1st and wrongly skipped the
        // first-player draw. The mirror is authoritative; this just lets the
        // local copy catch up before the phase reads it.
        host.setState({}, () => { if (!host._unmounted) host._runPhaseSteps(PHASE.START, newTurn); });
      }
    }
    // Reconciler parity harness (A4): dev-only drift check on the just-folded state
    // vs the projection. Warns (never throws) so a missing/diverged fold surfaces
    // to developers as Phase B breadth grows. No-op in prod + sandbox.
    assertReconcilerParity(host, m);
    // Render-parity checker: dev-only SCENE-vs-mirror invariants (board
    // meshes + cells, hand fans, pile stacks, solo turn pointer), deferred
    // to after-render so it sees the post-fold scene. Violations confirm
    // across a recheck window (skipping in-flight optimistic races), then
    // console.error + one toast per match + the dev match transcript.
    scheduleRenderParityCheck(host);
  };
  return foldAndApply;
}

export function setupSocketListeners(host) {
  if (host._socketListenersActive) return;
  host._socketListenersActive = true;

  // Replay playback: the ReplayDriver stands in for the live socket. It feeds
  // the same fold funnel (createFoldAndApply) with the same { full | diff }
  // messages, so the board renders a replay through the identical code path it
  // renders a live spectated match. Nothing else registers: no socket
  // channels, no presence relay, no recorder. A replay is a file, not a
  // session.
  const replayDriver = host.props.replayDriver || null;
  if (replayDriver) {
    const mirror = host._authMirror || (host._authMirror = new ClientMirror({
      // Gap heal: the driver re-emits its current frame as a Full, same
      // contract as asking the server for one.
      requestFull: () => replayDriver.requestFull(),
    }));
    const fold = createFoldAndApply(host, mirror, { live: false });
    replayDriver.attach((msg) => {
      // A seek converges via Full: forget played fights so stepping through
      // one again re-stages it (the scene itself is position-driven below).
      if (msg?.kind === 'full') host._playedFights?.clear?.();
      fold(msg);
    });
    // The combat result scene mirrors the playhead: staged while the current
    // frame is a fight's resolution (a paused playhead keeps it open, and a
    // click on the combat entry seeks here), torn down the moment the
    // position moves off it. The latch keeps heal-Fulls from re-staging a
    // scene the viewer dismissed by clicking it away.
    replayDriver.onCombat((env) => {
      if (env?.combatId) {
        if (host._replayCombatShown === env.combatId) return;
        host._replayCombatShown = env.combatId;
        host._playedFights?.delete?.(env.combatId);
        onReplayCombatEnvelope(host, env);
      } else {
        host._replayCombatShown = null;
        if (host.state.combatScene) host._closeCombatScene();
      }
    });
    return;
  }

  // When the underlying WebSocket reconnects mid-match (liveness timer
  // forced a reconnect, network hiccup, etc.), the server's session:active
  // path is a no-op because the GameBoard is already open. We have to
  // ask for fresh authoritative state ourselves. Otherwise any opponent
  // actions that occurred during the outage are lost.
  host._serverReconnectUnsub = onServerReconnect(() => {
    if (host._unmounted) return;
console.log('[GameBoard] server reconnected. Pulling authoritative state');
    // Re-pull the per-seat (or spectator) match:full so the authoritative
    // mirror repopulates. Any opponent actions during the outage fold in.
    host.requestAuthoritativeState();
    // Heal any combat record that diverged from the engine while we were
    // offline, and re-send an unconsumed decision. Don't wait for the next
    // reconcile tick. Deferred so it runs after the state sync adopt lands.
    setTimeout(() => { if (!host._unmounted) host._combatReconcileTick?.(); }, 1200);
  });

  // Spectators are read-only. They consume state:sync + game:action
  // mirrors from the host but must not react to player-lifecycle
  // events as if they were a player. Without these guards, when a
  // real player leaves the spectated room the spectator's GameBoard
  // would fire the "Match paused" toast, force-save (potentially
  // stamping the spectated state into a stale instance save), and
  // force-exit them as if THEIR opponent had abandoned.
  //
  // Spectator lifecycle: we still want to know when a player
  // drops/reconnects (toast, don't exit) and when the room is
  // destroyed (match ended or both players gone. Show the
  // end-of-match overlay).
  if (host.props.isSpectating) {
    onOpponentDropped((data) => {
      const seconds = Math.ceil((data?.gracePeriodMs || 60000) / 1000);
      toast.info(t('A player disconnected. Waiting up to {n}s for them to reconnect.', { n: seconds }), {
        id: 'spectator-opponent-dropped',
        duration: 5000,
      });
    });
    onPlayerJoined(() => {
      // Either player reconnected or initially connected. Keep
      // it informational, don't change view state.
      toast.success(t('A player reconnected.'), {
        id: 'spectator-opponent-reconnected',
        duration: 3000,
      });
    });
    onPlayerLeft((data) => {
      // Two flavours land here:
      //   • `permanent: true`. Room destroyed (both players
      //                          gone, grace expired, or the
      //                          match result was reported and
      //                          the draft tear-down ran). Match
      //                          is over from our perspective.
      //   • `permanent: false`. Draft room: one player left via
      //                          Save & Leave but the room stays
      //                          alive in case they (or their
      //                          opponent) rejoin. Toast and
      //                          keep watching the frozen board.
      if (data?.permanent) {
        host.setState({ spectatorMatchEnded: true });
      } else {
        toast.info(t('A player left the match.'), {
          id: 'spectator-player-left',
          duration: 4000,
        });
      }
    });
  }
  if (!host.props.isSpectating) {
  onPlayerJoined(() => {
    host.setState((state) => ({
      connectionStatus: 'connected',
      matchStartTime: state.matchStartTime || Date.now(),
    }));
    // Solo: the "opponent" is the pilot themself (room:joined reports
    // opponentInRoom for the one member). Skip the toast.
    if (!host.props.isCasualSandbox) toast.success(t('Opponent connected'));
  });

  onOpponentDropped((data) => {
    // Temporary disconnect. Freeze the board and show a reconnection
    // overlay. The grace period timer counts down on-screen so the
    // remaining player knows what's happening.
    const gracePeriodMs = data.gracePeriodMs || 60000;
    clearInterval(host._disconnectCountdown);
    host.setState({ opponentDisconnected: true, opponentDisconnectTimer: Math.round(gracePeriodMs / 1000) });
    host._disconnectCountdown = setInterval(() => {
      host.setState((s) => {
        const next = (s.opponentDisconnectTimer || 0) - 1;
        if (next <= 0) {
          clearInterval(host._disconnectCountdown);
          return { opponentDisconnectTimer: 0 };
        }
        return { opponentDisconnectTimer: next };
      });
    }, 1000);
  });

  onPlayerJoined(() => {
    // Opponent reconnected. Unfreeze the board.
    clearInterval(host._disconnectCountdown);
    if (host.state.opponentDisconnected) {
      host.setState({ opponentDisconnected: false, opponentDisconnectTimer: null });
      toast.success(t('Opponent reconnected'));
    }
  });

  onPlayerLeft(() => {
    // Opponent's room membership ended. Either they closed the
    // app, hit Save & Leave, or their grace-period timer
    // expired. Under the unified instance model this is
    // ALWAYS a pause, never a forfeit: the match stays in the
    // save list for both sides to resume later. The remaining
    // player is shown a clear "host left. Match paused"
    // message and sent back to SessionBrowser. If they want
    // the win for a truly-absent opponent, 72 h grace unlocks
    // a Claim Win button on the save row.
    clearInterval(host._disconnectCountdown);
    try { localStorage.removeItem('valkenhall-active-session'); } catch {}
    host.setState({ opponentDisconnected: false, opponentDisconnectTimer: null });
    // Save locally before exit so the host's blob (when this
    // client IS the host of an instance match) is current. The
    // no-op is harmless on guests.
    // Suppress the "match paused" toast (and the auto-exit) when the
    // match has already concluded. Either the reward modal is open
    // (showMatchResult) or rewards have been applied (_matchConcluded).
    // In those cases the peer's disconnect is the expected post-claim
    // teardown, not a genuine mid-match abandon: the toast would land
    // on top of the reward tally and the auto-exit would yank the
    // tally out from under the user before they could read it. The
    // reward modal owns the exit (via onMatchComplete → onExit) and
    // fires it when the user clicks Continue.
    const matchAlreadyEnded = host._matchConcluded || host.state.showMatchResult;
    const savePromise = host.state.isHost ? host.autoSave({ force: true }).catch(() => {}) : Promise.resolve();
    savePromise.then(() => {
      disconnectSocket();
      if (matchAlreadyEnded) return;
      toast.info(t('Match paused. Your opponent left. You can resume it later from your save list.'), {
        duration: 6000,
      });
      host.props.onExit?.();
    });
    return;
    // eslint-disable-next-line no-unreachable
    if (false) { // legacy auto-win code, preserved for reference
      if (host.props.isRankedMatch || host.props.isArenaMatch) {
        toast('Opponent left. You win!');
        host.setState({ showMatchResult: true }, () => {
          if (host.matchResultRef) {
            host.matchResultRef.applyRewards(true, { silent: true });
          }
        });
      } else {
        disconnectSocket();
        toast.error(t('Opponent left. Session ended.'));
        host.props.onExit();
      }
    }
  });

  } // end if (!isSpectating). Player-lifecycle handlers

  // Durable combat record delivery. The ONLY combat sync channel (the
  // CombatEngine flow has no legacy fallback), so it registers
  // unconditionally regardless of the auth-match snapshot flag.
  onMatchCombat((data) => {
    if (host._unmounted || !host.scene) return;
    // The server envelope carries { events, combatId, attackerId, deaths }
    // (no `.combat` record. Board state rides the diff). Play the scene.
    onAuthCombatEvents(host, data);
  });

  if (isAuthMatchEnabled()) {


    // Server-authoritative turn/phase read path (Phase 1, Task 3). A single
    // ClientMirror folds inbound match:diff / match:full wire messages with
    // the same vendored reducer the server uses; on a version gap it asks the
    // server for a fresh per-seat Full (gap heal). After each fold we map the
    // authoritative phase/rollOff onto the GameBoard fields the renderer reads
    // (matchPhase, currentTurn/activePlayerNum, turnNumbers, currentPhase,
    // firstPlayerRollState). The mapper is idempotent, so a redundant fold is
    // a no-op for the renderer.
    const mirror = host._authMirror || (host._authMirror = new ClientMirror({
      requestFull: () => requestMatchFull(host._matchKey()),
    }));
    const foldAndApply = createFoldAndApply(host, mirror);
    onMatchDiff(foldAndApply);
    onMatchFull(foldAndApply);

    // Server declined an intent: name the reason in the advisory log so a
    // bounced card reads as an explained refusal, never a silent bug. The
    // pending sweep / snap already handles the visual revert.
    const REJECT_COPY = {
      authz: k('You can\'t do that right now. It may not be your turn.'),
      unreachable: k('That square is out of reach.'),
      out_of_reach: k('That square is out of reach.'),
      tapped: k('It is tapped and can\'t move.'),
      summoning_sick: k('It was just summoned and can\'t move yet.'),
      region_forbidden: k('It can\'t go to that region.'),
      positional: k('It can\'t be cast to that square.'),
      unaffordable: k('Not enough mana or thresholds.'),
      site_banned_cell: k('A site can\'t be placed there.'),
      site_grant_restriction: k('Your site play this turn doesn\'t allow that site.'),
      owner_locked: k('Only its owner can cast that.'),
      owner_element: k('Its owner can\'t provide the element for that.'),
      owner_summon_cell: k('It can\'t be summoned to that square.'),
    };
    onMatchIntentRejected((data) => {
      if (host._unmounted) return;
      // Resolve the card BEFORE the revert (the cast revert removes its mesh).
      const card = data?.cardId ? host.meshes.get(data.cardId)?.userData?.cardInstance : null;
      // Revert the optimistic apply NOW. A rejected cast bounces straight
      // back to hand, a rejected move snaps straight to its server cell. The
      // TTL sweeps stay as the fallback for a lost rejection message; before
      // this, the reject was log-only and the player watched a phantom card
      // sit on the board until the sweep (or forever, if they re-dragged it
      // across the sweep tick).
      if (data?.domain === 'cast') revertPendingCastNow(host, data.cardId);
      else if (data?.domain === 'movement') revertPendingMoveNow(host, data.cardId);
      const what = card?.name || msg('That action');
      const why = REJECT_COPY[data?.reason] || k('The server declined it.');
      host.addAdvisoryLog('{what} was refused. {why}', card || undefined, { what, why: msg(why) });
    });
  }


  // Listen for remote game actions. Broadcast=false to prevent re-emit loops
  const actionHandlers = {
    'card:place': (data) => {
      const instance = data.cardInstance;
      // Pure state replication. The actor produces any log line via
      // logAction on its own (broadcast on 'logbook:entry'). The sound
      // cue still fires here so the opponent's table feels reactive
      // for fully-materialized placements (status-token spawns, deck
      // draws, etc.). Staged drags get their cue from card:release.
      if (!data.staged && !data.silent) {
        playSound('cardPlace');
      }
      host.addCardToTable(instance, false);
      // Only mark remote-owned for STAGED placements. Those are
      // immediately followed by a card:claim/pose/release flow and
      // need ownership=remote so the interpolation buffer drives Y.
      // Non-staged placements (avatar spawn, deck draw, etc.) never
      // get a card:release, so setting them remote would lock them
      // out of _applyComputedY forever. The card's Y would be stuck
      // at its initial position and clip through everything else
      // since the rank-based stack-Y override would be skipped.
      // interactionOrder arrived in instance from the placer;
      // addCardToTable advanced our local counter past it.
      if (data.staged) {
        host.ownership.setRemote(instance.id);
      }
      // Non-staged placements arrive with a final pose, so recompute
      // immediately. Staged placements get their position via the
      // following card:release stream. That's where the recompute fires.
      if (!data.staged) {
        host.recomputeStaticAuras();
      }
    },
    // The opponent is taking ownership of a card. Switch our copy to
    // remote-controlled mode (kinematic, non-colliding) so local physics
    // doesn't fight the incoming pose stream. If we currently believe
    // we own the card, yield. Last claim wins. The rare double-grab
    // race resolves itself within one round trip.
    'card:claim': (data) => {
      perf.count('sync.claim.recv');
      const mesh = host.meshes.get(data.cardId);
      if (!mesh) return;
      const body = mesh.userData?.body;
      if (!body) return;
      // If ANY card in our active drag (single or multi) was just
      // claimed by the opponent, abort the whole drag. Last claim
      // wins. Checking draggingIds handles the multi-card case so
      // a late claim on a group member still yields cleanly.
      if (host.dragging
        && (host.dragging.draggingIds
          ? host.dragging.draggingIds.has(data.cardId)
          : host.dragging.cardInstance?.id === data.cardId)) {
        host.dragging = null;
        host.clearCemeteryDragHover();
        host.clearDeckDragHover();
      }
      host.ownership.setRemote(data.cardId);
      // Apply the bumped interaction order so this client's
      // _computeStackedY produces the same rank as the sender's.
      // Advance the local counter past it so any subsequent local
      // claim still allocates an order strictly above.
      if (typeof data.interactionOrder === 'number') {
        const card = mesh.userData?.cardInstance;
        if (card) card.interactionOrder = data.interactionOrder;
        if (data.interactionOrder > host._interactionCounter) {
          host._interactionCounter = data.interactionOrder;
        }
      }
    },
    // Pose snapshot for a remote-owned card. Buffered for interpolation;
    // the per-frame loop reads samples at (now - render delay) so motion
    // appears smooth even when packets arrive jittered.
    'card:pose': (data) => {
      if (!Array.isArray(data?.pos) || !Array.isArray(data?.quat)) return;
      perf.count('sync.pose.recv');
      // Only buffer if we currently believe the card is remote-owned;
      // a stray pose for a free/local card would corrupt our state.
      if (!host.ownership.is(data.cardId, 'remote')) return;
      host.ownership.pushSnapshot(data.cardId, data.pos, data.quat, performance.now());
    },
    // Opponent's card has settled. Apply the authoritative final pose,
    // return the body to dynamic + colliding, sleep it, and mark the
    // card free so either side can wake it again on next interaction.
    'card:release': (data) => {
      perf.count('sync.release.recv');
      const mesh = host.meshes.get(data.cardId);
      if (!mesh) return;
      const body = mesh.userData?.body;
      if (!body) return;
      if (Array.isArray(data?.pos) && Array.isArray(data?.quat)) {
        // Apply the released pose to the body so the broadcast loop
        // has the latest XZ for any future updates. Y is overridden
        // by _applyComputedY anyway; what matters is X and Z.
        applyRemotePoseAndRest(body, data.pos, data.quat);
        syncMeshFromBody(mesh);
      }
      // Sync cardInstance.x/z to the post-release pose. Without this
      // the opponent's instance retains its staged drag-start coords
      // and every position-dependent local computation goes stale.
      // most visibly: aura `_auraAnchor` (Cursed Iron's detach lock
      // depends on it) and every "what cell is this card on" query.
      const card = mesh.userData?.cardInstance;
      if (card && Array.isArray(data?.pos)) {
        card.x = data.pos[0];
        card.z = data.pos[2];
        // Re-derive aura anchor from the new position so the lock
        // set lockedEquipmentIds builds is consistent across clients.
        if (card.type === 'Aura') {
          const grid = getGameGrid(host.state.spawnConfig);
          if (grid) {
            const { colPositions, rowPositions } = computeGridPositions(grid);
            const anchor = resolveAuraAnchor(
              card, grid, colPositions.length - 1, rowPositions.length - 1, host._cellAtWorld,
            );
            if (anchor) card._auraAnchor = anchor;
          }
        }
      }
      // Optional extras payload from programmatic moves. Wildfire's
      // auto-step sends `_visitedCells` so both clients agree on
      // which cells are "fresh" for the next jump. Without this, each
      // side's `after_each_turn` would pick a destination using a
      // divergent visited list and Wildfire would oscillate.
      if (card && data?.extras && typeof data.extras === 'object') {
        if (Array.isArray(data.extras._visitedCells)) {
          card._visitedCells = [...data.extras._visitedCells];
        }
      }
      // Only play sound if the card was recently claimed (< 2s ago).
      // Physics micro-settles on stacked cards cause rapid claim→
      // release cycles that fill the board with constant clicking.
      const claimAge = performance.now() - (host.ownership.claimTime(data.cardId) || 0);
      if (claimAge < 2000) playSound('cardPlace');
      host.ownership.setFree(data.cardId);
      syncMeshFromBody(mesh);
      // A rejected move (authz, unreachable) sends no diff. Reconcile the
      // relayed ghost pose back to server truth after the pending window.
      scheduleReleaseReconcile(host, data.cardId);
      // Position changed. "nearby" / "adjacent" scope auras may have
      // gained or lost targets. Cheap pass; covers staged plays landing
      // and intra-board moves alike.
      host.recomputeStaticAuras();
    },
    'card:remove': (data) => {
      const mesh = host.meshes.get(data.cardId);
      if (mesh) {
        // Strip the card out of any lock-constraint chain before
        // disposing the body so we never leave a constraint
        // pointing at a destroyed body.
        host.evictCardFromGroup(data.cardId);
        removeCardBody(host.physicsWorld, mesh);
        host.meshes.delete(data.cardId);
        host._removeAllCardEffects(data.cardId);
        // Departure parity: when the sender names the destination pile,
        // play the same fly-to-pile flight it showed locally (the
        // sender staggers multi-death removals, so the procession to
        // the cemetery reads identically on this side). Fallback to an
        // instant despawn when the pile mesh isn't known yet (e.g. a
        // freshly created cemetery whose pile:sync hasn't landed).
        const pm = data.toPileId ? host.pileMeshes.get(data.toPileId) : null;
        if (pm) {
          animateCardToPile(mesh, pm.position.x, pm.position.z, host.scene.scene, () => {});
        } else {
          host.scene.scene.remove(mesh);
          mesh.geometry?.dispose();
          const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
          mats.forEach((m) => m.dispose());
        }
      } else if (host._exitingMeshes?.has(data.cardId)) {
        // The mesh already left host.meshes via a local departure and was
        // tracked as exiting; the authoritative removal confirms it's gone,
        // so finalize the (possibly gate-stranded) exit now rather than
        // waiting out the reconciler's deadline. The ghost fix's fast path.
        host._disposeCardMesh(host._exitingMeshes.get(data.cardId).mesh);
        host._exitingMeshes.delete(data.cardId);
      }
      host.lifeHUDs.delete(data.cardId);
      host.statusHUDs.delete(data.cardId);
      host.counterHUDs.delete(data.cardId);
      host.selectedCardIds.delete(data.cardId);
      host._yTweens.delete(data.cardId);
      // A card leaving the realm may have been an aura source. Drop
      // its contributions and refresh affected siblings.
      host.recomputeStaticAuras();
      // Backstop: any orphan this authoritative removal implies gets swept.
      host._requestBoardReconcile();
    },
    // Remote player created or updated a group. Apply the same
    // assignment locally so the cards drag together on both boards.
    // Suppress the outbound broadcast that assignGroup would otherwise
    // trigger. We're applying a remote event, not originating one.
    'group:set': (data) => {
      if (!data?.groupId || !Array.isArray(data.cardIds)) return;
      host.sync.withSuppressed(() => {
        host.assignGroup(data.groupId, data.cardIds);
      });
    },
    'group:clear': (data) => {
      if (!data?.groupId) return;
      host.sync.withSuppressed(() => {
        host.dissolveGroup(data.groupId);
      });
    },
    // Opponent hover highlight. When the other player hovers or
    // inspects a card, we tint its emissive so it's visible on our
    // board. Uses a distinct blue tone to differentiate from local
    // hover (gray 0x222222) and selection (gold 0x5a3f0a).
    'card:hover': (data) => {
      const OPPONENT_HOVER_COLOR = 0x0f2a55;

      // Clear previous opponent highlight
      if (host.opponentHoveredCardId) {
        const prevMesh = host.meshes.get(host.opponentHoveredCardId);
        if (prevMesh) {
          const mats = Array.isArray(prevMesh.material) ? prevMesh.material : [prevMesh.material];
          const isSelected = host.selectedCardIds.has(host.opponentHoveredCardId);
          const isLocalHover = host.hoveredMesh?.userData?.cardInstance?.id === host.opponentHoveredCardId;
          const restoreHex = isSelected ? 0x5a3f0a : isLocalHover ? 0x222222 : 0x000000;
          for (const m of mats) { if (m.emissive) m.emissive.setHex(restoreHex); }
        }
      }

      host.opponentHoveredCardId = data.cardId || null;

      // Apply new opponent highlight
      if (host.opponentHoveredCardId) {
        const mesh = host.meshes.get(host.opponentHoveredCardId);
        if (mesh) {
          const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
          for (const m of mats) { if (m.emissive) m.emissive.setHex(OPPONENT_HOVER_COLOR); }
        }
      }
    },

    'log:chat': (data) => {
      const text = (data?.text || '').trim();
      if (!text) return;
      host.addLogEntry(text, 'opponent', 'chat');
    },
    // Opponent activated an ability. Mirror it onto our storyline as a
    // read-only acknowledge so we register what was activated and clear
    // it at our pace.
    'storyline:ability': (data) => {
      host._enqueueOpponentAbilityNotice(data);
    },
    // Centralised logbook propagation. The sender calls
    // GameBoard.logAction / logSystem which appends locally and fires
    // this message; we mirror it with the actor flipped to 'opponent'
    // for player-driven actions and 'system' for rule announcements.
    // State-mutation messages (card:subsurface, card:tap, card:moved, …)
    // stay focused on state. Logbook lines no longer have to be
    // reconstructed by every individual handler.
    'logbook:entry': (data) => {
      const text = (data?.text || '').trim();
      if (!text && typeof data?.key !== 'string') return;
      const kind = data?.kind || 'action';
      // Rules-engine warnings only mean something when the engine is the
      // one applying the rules. A manual session drops them.
      if (kind === 'warning' && !host._automationsEnabled()) return;
      const actor = data?.system ? 'system' : 'opponent';
      // A message renders in this seat's language; a text-only line from an
      // older peer renders as sent.
      host.addLogEntry(typeof data.key === 'string' ? { key: data.key, values: data.values } : text, actor, kind);
    },
    // Board ping from the opponent. Always renders as gold; opponent's
    // pings play louder than your own so the audio cue tells you who
    // pinged. Drop pings older than 3s as a safety net for network
    // burps and reconnect floods. But if the very first ping is
    // already stale, the opponent's clock is probably skewed: warn
    // once and stop stale-checking for the rest of the session rather
    // than silently muting them forever.
    'ping:fire': (data) => {
      if (!host.pingLayer || !data) return;
      if (typeof data.t === 'number' && Date.now() - data.t > 3000) {
        if (!host._pingStaleWarned) {
          host._pingStaleWarned = true;
          console.warn('[ping] received stale ping. Disabling stale check (clock skew suspected)');
        } else {
          return;
        }
      }
      host.pingLayer.fire({ x: data.x, y: data.y, z: data.z });
      try { playUI(UI.CLICK_SOFT, { volume: 0.9 }); } catch {}
    },
    'deck:spawn': (data) => {
      if (!data.deck) return;
      // P2P sandbox path: only the opponent's spawn ever arrives here
      // (the host suppresses its own broadcast in withSuppressed).
      // Hand it to the gate via _queueOpponentSpawn so phase 2 runs
      // before the cards land on the table.
      host._queueOpponentSpawn(data.deck, data.playerNum);
    },
    // match:settled is the server's authoritative settlement push,
    // delivered when arbitration completes (both clients reported,
    // or this client's report was the first and the opponent's WS
    // timed out). Carries this player's reward envelope. ArenaMatchResult
    // subscribes via the onSettlementSubscribe prop wired below and
    // we forward the envelope through that bus.
    //
    // Spectators have no envelope to claim. They just need the
    // end-of-match overlay so they can leave the room cleanly.
    'match:settled': (data) => {
      if (host.props.isSpectating) {
        host.setState({ spectatorMatchEnded: true });
        return;
      }
      // A settlement can arrive while the opponent-disconnected overlay is
      // up: the grace period expired and the server awarded this player a
      // win by default. Tear the overlay + countdown down so the result
      // screen below isn't rendered behind it.
      clearInterval(host._disconnectCountdown);
      if (host.state.opponentDisconnected) {
        host.setState({ opponentDisconnected: false, opponentDisconnectTimer: null });
      }
      const envelope = data?.envelope || null;
      if (host._matchSettledSubscriber && envelope) {
        host._matchSettledSubscriber(envelope);
      } else {
        host._pendingMatchSettlement = envelope;
      }
      // Make sure the overlay is mounted even if the local engine
      // didn't detect a death-blow (lone-reporter timeout path).
      if (!host.state.showMatchResult) {
        host.setState({
          autoMatchWinner: envelope?.won ? 'me' : 'opponent',
          showMatchResult: true,
        });
      }
    },
    // Dispute reprompt. Both clients reported "I won." The server
    // wiped both reports and is asking each side to confirm again.
    // Forward to ArenaMatchResult, which resets to the picker.
    'match:dispute_reprompt': () => {
      if (host.props.isSpectating) return;
      if (host._matchDisputeRepromptSubscriber) {
        host._matchDisputeRepromptSubscriber();
      } else {
        host._pendingMatchDisputeReprompt = true;
      }
      // Ensure the overlay is up so the picker has a place to render.
      if (!host.state.showMatchResult) {
        host.setState({ showMatchResult: true });
      }
    },
    // The arbitration layer also nudges the opponent over WS when a
    // lone report has been sitting unmatched. We open the result
    // overlay so the local player gets a chance to report (or the
    // engine auto-reports) before the final 30s timeout settles
    // unilaterally in the other side's favour.
    'match:report_request': () => {
      if (host.props.isSpectating) return;
      if (host.state.showMatchResult) return;
      // Without an engine-determined winner we cannot auto-report;
      // surface the overlay in the "Confirming with server" state
      // so the player at least knows their match is being settled.
      host.setState({ showMatchResult: true });
    },
    // The match was voided (no-contest). A present player chose
    // "Forfeit and leave" from the opponent-disconnected overlay instead
    // of taking the win by default. No ranking change for anyone. Both
    // sides return to the hub with a neutral notice. Mirrors the
    // onPlayerLeft exit, minus the "paused, resume later" framing. A
    // void is terminal, there is nothing to resume.
    'match:voided': () => {
      if (host.props.isSpectating) {
        host.setState({ spectatorMatchEnded: true });
        return;
      }
      clearInterval(host._disconnectCountdown);
      try { localStorage.removeItem('valkenhall-active-session'); } catch {}
      host.setState({ opponentDisconnected: false, opponentDisconnectTimer: null });
      const savePromise = host.state.isHost ? host.autoSave({ force: true }).catch(() => {}) : Promise.resolve();
      savePromise.then(() => {
        disconnectSocket();
        if (host._matchConcluded || host.state.showMatchResult) return;
        toast.info(t('Match voided. No ranking change.'), { duration: 6000 });

        host.props.onExit?.();
      });
    },
    // Opponent clicked "End Match" on their side. Run the same
    // local resolution flow so our modal also opens. Either with
    // an engine-determined autoMatchWinner (rare while the engine
    // is still WIP) or with the "Who won?" picker. The handler is
    // idempotent via the showMatchResult guard in _endMatchManually.
    //
    // Spectators get the end-of-match overlay instead, like every other
    // match-lifecycle signal: _endMatchManually bypasses the spectator branch
    // in _showGameOverUI and would mount the result modal, which auto-submits
    // a report for a match this seat never played.
    'match:end-request': () => {
      if (host.props.isSpectating) {
        host.setState({ spectatorMatchEnded: true });
        return;
      }
      host._endMatchManually();
    },
  };

  // ── Match log capture (M1) ──────────────────────────────────
  // Record every real two-player match (constructed match, draft, sealed)
  // for card play/win-rate analytics. Not just ranked/arena games. The
  // per-card facts carry mode + format, so constructed vs limited data stays
  // separable downstream and avatar stats can exclude draft/sealed.
  // Excluded: spectators (would log someone else's game) and sandbox, which
  // is the dual-hand solo practice mode where one player controls both sides
  // (its outcomes are meaningless and would pollute win rates).
  const shouldRecord = !host.props.isSpectating && !host.props.isCasualSandbox;
  // Dev transcript: point the client logger at this match so onAnySend / inbound
  // taps + errors stream into .devlogs/<matchId>/client-<seat>.jsonl.
  const devOn = isDevBuild();
  if (devOn) { setDevLogMatch(host._matchKey?.() || host.state.roomCode); installDevErrorHook(); }
  if (shouldRecord) {
    host.matchRecorder.start({
      roomCode: host.state.roomCode,
      seat: host.state.isHost ? 'host' : 'guest',
      meta: {
        mode: host.props.instanceMode || 'match',
        format: host.props.format || null,
        avatarId: host.props.avatarId || null,
        deckElements: host.props.deckElements || [],
      },
      initialSnapshot: host.serializeTableState(),
    });
  }
  // Outbound tap: dev-log + (for real matches) record every action the bridge sends.
  host.sync.onAnySend = (type, data) => {
    if (devOn) devLog('send', { type, data });
    if (shouldRecord) host.matchRecorder.recordEvent({ dir: 'out', type, data });
  };
  // Inbound tap: dev-log + record each dispatched remote action, then run the
  // real handler. Wrapping here keeps the handler map itself clean.
  const wrappedHandlers = {};
  for (const [type, fn] of Object.entries(actionHandlers)) {
    wrappedHandlers[type] = (data) => {
      if (devOn) devLog('recv', { type, data });
      if (shouldRecord) host.matchRecorder.recordEvent({ dir: 'in', type, data });
      return fn(data);
    };
  }
  host.sync.registerHandlers(wrappedHandlers);
  host._registerCombatHandlers();
}
